import { useState, useEffect, useCallback } from 'react';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { PregnancyInfoData, PregnancyInfoResponse, hasAnyData } from '../types';

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pregnancy-info`;

export function usePregnancyInfo() {
  const { currentPregnancy, lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { session, isLoading: isAuthLoading } = useAuth();
  const [data, setData] = useState<PregnancyInfoData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasData, setHasData] = useState(false);

  // In baby mode, use lastCompletedPregnancy; otherwise use currentPregnancy
  const isBabyMode = appMode === 'baby' && lastCompletedPregnancy?.completion_type === 'birth';
  const activePregnancyId = currentPregnancy?.id || (isBabyMode ? lastCompletedPregnancy?.id : null);

  // Daten laden
  const fetchData = useCallback(async () => {
    // Warte auf Auth-Initialisierung
    if (isAuthLoading) {
      return;
    }

    if (!activePregnancyId) {
      setIsLoading(false);
      setData(null);
      setHasData(false);
      return;
    }

    if (!session) {
      setIsLoading(false);
      setData(null);
      setHasData(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {

      const response = await fetch(
        `${FUNCTION_URL}?pregnancy_id=${activePregnancyId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session!.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Laden');
      }

      const result: PregnancyInfoResponse = await response.json();
      setData(result.data);
      setHasData(result.exists && hasAnyData(result.data));
    } catch (err) {
      console.error('Error fetching pregnancy info:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      setData(null);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  }, [activePregnancyId, session, isAuthLoading]);

  // Initial laden
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Daten speichern
  const saveData = useCallback(async (newData: PregnancyInfoData): Promise<boolean> => {
    if (!activePregnancyId) {
      setError('Keine Schwangerschaft aktiv');
      return false;
    }

    if (!session) {
      setError('Nicht angemeldet');
      return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pregnancy_id: activePregnancyId,
          data: newData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Speichern');
      }

      setData(newData);
      setHasData(hasAnyData(newData));
      return true;
    } catch (err) {
      console.error('Error saving pregnancy info:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [activePregnancyId, session]);

  // Alle Daten löschen
  const deleteData = useCallback(async (): Promise<boolean> => {
    if (!activePregnancyId) {
      setError('Keine Schwangerschaft aktiv');
      return false;
    }

    if (!session) {
      setError('Nicht angemeldet');
      return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${FUNCTION_URL}?pregnancy_id=${activePregnancyId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Fehler beim Löschen');
      }

      setData(null);
      setHasData(false);
      return true;
    } catch (err) {
      console.error('Error deleting pregnancy info:', err);
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [activePregnancyId, session]);

  return {
    data,
    isLoading,
    isSaving,
    error,
    hasData,
    isBabyMode,
    fetchData,
    saveData,
    deleteData,
  };
}
